import React, { useContext, useState, useEffect } from 'react';
import { Typography, Grid, Card, CardContent, CardMedia, CardActionArea, Box, TextField, Button, IconButton, Collapse, useMediaQuery, useTheme } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import StateContext from "../../Contexts/StateContext";
import Axios from 'axios';
import config from '../../config';

const sources = [
  { name: 'FoxNews', url: 'https://www.foxnews.com', preview: 'https://www.foxnews.com/favicon.ico' },
  { name: 'Háromszék', url: 'https://www.3szek.ro', preview: 'https://www.3szek.ro/favicon.ico' },
  { name: 'Index', url: 'https://www.index.hu', preview: 'https://www.index.hu/favicon.ico' },
  { name: 'HotNews', url: 'https://www.hotnews.ro', preview: 'https://www.hotnews.ro/favicon.ico' },
  { name: 'France Info', url: 'https://www.francetvinfo.fr', preview: 'https://www.francetvinfo.fr/favicon.ico' },
  { name: 'Corriere della Sera', url: 'https://www.corriere.it', preview: 'https://www.corriere.it/favicon.ico' },
  { name: 'El Periodico', url: 'https://www.elperiodico.com', preview: 'https://www.elperiodico.com/favicon.ico' },
  { name: 'FAZ', url: 'https://www.faz.net', preview: 'https://www.faz.net/favicon.ico' },
];

function News() {
  const { t } = useTranslation();
  const globalState = useContext(StateContext);
  const [userLinks, setUserLinks] = useState([]);
  const [newLink, setNewLink] = useState({ title: '', url: '', image_url: '' });
  const [showAllLinks, setShowAllLinks] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const apiBase = process.env.NODE_ENV === 'production' ? config.production : config.development;

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await Axios.get(apiBase.newsLinksEndpoint);
        setUserLinks(response.data);
      } catch (e) {
        console.error("Error fetching news links", e);
      }
    };
    fetchLinks();
  }, [apiBase.newsLinksEndpoint]);

  const handleAddLink = async () => {
    if (newLink.title && newLink.url) {
      try {
        const token = globalState.jwtAccessToken || localStorage.getItem("jwtAccessToken");
        if (!token) {
          console.error("No access token found");
          return;
        }
        const response = await Axios.post(apiBase.newsLinksEndpoint, newLink, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserLinks([response.data, ...userLinks]);
        setNewLink({ title: '', url: '', image_url: '' });
      } catch (e) {
        console.error("Error adding news link", e.response ? e.response.data : e);
      }
    }
  };

  const fetchOGData = async (url) => {
    if (!url) return;
    try {
      // Megpróbáljuk lekérni az oldal tartalmát, hogy kinyerjük az OG tageket.
      // Mivel kliens oldalról a CORS korlátozások miatt ez gyakran meghiúsul,
      // a legjobb megoldás egy proxy vagy a backend használata lenne.
      // Egyelőre egy egyszerű regex alapú keresést implementálunk, hátha...
      const response = await Axios.get(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}`);
      const html = response.data.contents;
      
      const titleMatch = html.match(/<meta property="og:title" content="([^"]+)"/i);
      const imageMatch = html.match(/<meta property="og:image" content="([^"]+)"/i);
      
      if (titleMatch && !newLink.title) {
        setNewLink(prev => ({ ...prev, title: titleMatch[1] }));
      }
      if (imageMatch && !newLink.image_url) {
        setNewLink(prev => ({ ...prev, image_url: imageMatch[1] }));
      }
    } catch (e) {
      console.error("Error fetching OG data", e);
    }
  };

  const handleDeleteLink = async (id) => {
    try {
      const token = globalState.jwtAccessToken || localStorage.getItem("jwtAccessToken");
      if (!token) {
        console.error("No access token found");
        return;
      }
      await Axios.delete(apiBase.deleteNewsLinkEndpoint(id), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserLinks(userLinks.filter(link => link.id !== id));
    } catch (e) {
      console.error("Error deleting news link", e.response ? e.response.data : e);
    }
  };

  const visibleLinks = showAllLinks ? userLinks : userLinks.slice(0, 8);

  return (
    <Box sx={{ flexGrow: 1, p: isMobile ? 1 : 3, mt: '50px' }}>
      <Typography variant={isMobile ? "h5" : "h3"} align="center" gutterBottom sx={{ fontWeight: 'bold', mt: isMobile ? 2 : 0 }}>
        {t('News.Title')}
      </Typography>

      {/* Szerkesztői ajánló legfelül */}
      <Box sx={{ mb: 6 }}>
        <Typography variant={isMobile ? "h6" : "h5"} gutterBottom align="center">
          {t('News.EditorRecommendations')}
        </Typography>
        <Grid container spacing={isMobile ? 1 : 2} justifyContent="center">
          {globalState.userIsLogged && (
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>{t('News.AddNewLink')}</Typography>
                <TextField
                  fullWidth
                  label={t('News.LabelTitle')}
                  variant="outlined"
                  size="small"
                  value={newLink.title}
                  onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t('News.LabelURL')}
                  variant="outlined"
                  size="small"
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  onBlur={(e) => fetchOGData(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t('News.LabelImageURL')}
                  variant="outlined"
                  size="small"
                  value={newLink.image_url}
                  onChange={(e) => setNewLink({ ...newLink, image_url: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <Button 
                  fullWidth 
                  variant="contained" 
                  startIcon={<AddLinkIcon />}
                  onClick={handleAddLink}
                >
                  {t('News.AddButton')}
                </Button>
              </Card>
            </Grid>
          )}
          
          <Grid item xs={12} md={globalState.userIsLogged ? 8 : 10}>
            <Grid container spacing={isMobile ? 1 : 2}>
              {userLinks.length > 0 ? (
                <>
                  {visibleLinks.map((link) => (
                    <Grid item xs={12} sm={6} md={3} key={link.id}>
                      <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <CardActionArea component="a" href={link.url} target="_blank" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                          {link.image_url && (
                            <CardMedia
                              component="img"
                              height={isMobile ? "140" : "140"}
                              image={link.image_url}
                              alt={link.title}
                            />
                          )}
                          <CardContent sx={{ py: 1, flexGrow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.9rem' : '1rem' }} noWrap>{link.title}</Typography>
                            <Typography variant="caption" color="textSecondary" noWrap display="block">{link.url}</Typography>
                          </CardContent>
                        </CardActionArea>
                        {globalState.userIsLogged && (
                          <Box sx={{ p: 1, textAlign: 'right', borderTop: '1px solid #eee' }}>
                            <IconButton onClick={() => handleDeleteLink(link.id)} color="error" size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body1" align="center" color="textSecondary">
                    {t('News.NoLinksYet')}
                  </Typography>
                </Grid>
              )}
            </Grid>
            {userLinks.length > 8 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => setShowAllLinks(!showAllLinks)}
                  startIcon={showAllLinks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {showAllLinks ? t('News.ShowLess') : t('News.ShowMore')}
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Fix honlapok alul */}
      <Grid container spacing={isMobile ? 2 : 3}>
        {sources.map((source) => (
          <Grid item xs={12} sm={6} md={3} key={source.name}>
            <Typography variant={isMobile ? "h6" : "h5"} gutterBottom align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {source.name}
            </Typography>
            <Card sx={{ height: isMobile ? 400 : 600, display: 'flex', flexDirection: 'column' }}>
              <CardActionArea 
                component="a" 
                href={source.url} 
                target="_blank" 
                sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <CardMedia
                  component="img"
                  sx={{ width: 32, height: 32, mb: 1 }}
                  image={source.preview}
                  alt={source.name}
                />
              </CardActionArea>
              <Box sx={{ flexGrow: 1, overflow: 'hidden', borderTop: '1px solid #eee' }}>
                <iframe 
                  src={source.url} 
                  title={source.name} 
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  sandbox="allow-scripts allow-same-origin"
                />
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default News;